import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useModules } from '../lib/modules';
import { NAV_ITEMS, pagesFor, type ModuleId } from '../shell/nav-config';
import { useCurrentSession } from '../stores/session';
import styles from './SectionHeader.module.css';

/**
 * A section's own header (Redesign Groups 6 and 7): the title, an optional line on what the
 * section is, and the section's pages as tabs. The tabs are the module bar's own pages
 * (`shell/nav-config.ts`), filtered by the same rule, so a page the viewer may not open or a
 * switched-off module's page is never a tab (#13, §L28) and the two can never disagree. They are
 * plain links, so moving between them is a component swap (CLAUDE.md §5a rule 5).
 *
 * The page around it gives the padding, so it sits in a list screen and a record alike.
 */

interface SectionHeaderProps {
  readonly moduleId: ModuleId;
  readonly title: string;
  readonly lede?: string;
  /** The tab to mark, by its address — also marks it on a record opened from it. */
  readonly current: string;
}

const EXACT = { exact: true, includeSearch: false } as const;

export function SectionHeader({ moduleId, title, lede, current }: SectionHeaderProps): ReactNode {
  const session = useCurrentSession();
  const modules = useModules();
  const item = NAV_ITEMS.find((candidate) => candidate.id === moduleId);
  const tabs = item ? pagesFor(item, session, modules) : [];

  return (
    <header className={styles.header}>
      <h1 className={styles.title}>{title}</h1>
      {lede && <p className={styles.lede}>{lede}</p>}
      {tabs.length > 0 && (
        <nav className={styles.tabs} aria-label={title}>
          {tabs.map((tab) => (
            // Exact, so the section's first page is not also marked under every page below it.
            <Link key={tab.to} to={tab.to} activeOptions={EXACT} className={styles.tab} aria-current={tab.to === current ? 'page' : undefined}>
              {tab.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}
